import { forwardRef, ReactNode, useMemo } from 'react';

import {
    Box,
    CloseButton,
    Field,
    Input as ChakraInput,
    InputGroup,
    Spinner,
} from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { InputProps } from './input.props';

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            errorMessage,
            isClearable = false,
            isSubmitted = false,
            isLoading = false,
            showValidationIcons = true,
            inputProps = {},
            labelProps = {},
            clearButtonProps = {},
            leftIcon,
            onClearClick,
            rightElement,
            className,
            children,
            required,
            isFullWidth,
            ...props
        }: InputProps,
        ref,
    ): ReactNode => {
        const startIcon = useMemo(() => {
            if (leftIcon) {
                return leftIcon;
            } else if (!showValidationIcons) {
                return undefined;
            }

            if (errorMessage) {
                return <IconsLinear.Close size={'xs'} color="red.solid" />;
            } else if (isSubmitted) {
                return <IconsLinear.Tick size={'sm'} color="green.solid" />;
            }
        }, [errorMessage, isSubmitted, leftIcon, showValidationIcons]);

        const { css: labelStyles, ...labelPropsInner } = labelProps;
        const { css: inputStyles, ...inputPropsInner } = inputProps;

        const endElement = useMemo(() => {
            if (rightElement) {
                return rightElement;
            } else if (isLoading) {
                return <Spinner color={'gray.600'} size={'sm'} mr={2} />;
            } else if (isClearable) {
                return (
                    <CloseButton
                        size="xs"
                        colorPalette={'gray'}
                        variant={'plain'}
                        onClick={onClearClick}
                        mr={-2}
                        {...clearButtonProps}
                    >
                        <IconsLinear.Close />
                    </CloseButton>
                );
            }

            return undefined;
        }, [isClearable, isLoading, onClearClick, rightElement]);

        return (
            <Field.Root className={`wc-input ${className ?? ''}`} {...props}>
                {label ? (
                    <Field.Label
                        css={{
                            fontSize: 'sm',
                            ...labelStyles,
                        }}
                        {...labelPropsInner}
                    >
                        {label}{' '}
                        {required ? (
                            <Box color={'red.solid'} as={'span'} aria-hidden="true">
                                *
                            </Box>
                        ) : null}
                    </Field.Label>
                ) : null}

                <InputGroup
                    flexFlow={isFullWidth ? 'column' : undefined}
                    startElement={startIcon}
                    endElement={endElement}
                >
                    {children ?? (
                        <ChakraInput
                            ref={ref}
                            {...inputPropsInner}
                            css={{
                                ...inputStyles,
                            }}
                            pl={showValidationIcons ? 'var(--input-height)' : undefined}
                        />
                    )}
                </InputGroup>

                <Field.ErrorText
                    {...(inputProps['data-testid']
                        ? { 'data-testid': `error-message-${inputProps['data-testid']}` }
                        : {})}
                >
                    {errorMessage}
                </Field.ErrorText>
            </Field.Root>
        );
    },
);
